/* Dawn owns variants, gallery, quantity rules, and cart submission. */
if (!customElements.get('dd-recipe-picker')) {
  customElements.define(
    'dd-recipe-picker',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-group]');
          if (!button) return;
          this.querySelectorAll('[data-group]').forEach((tab) =>
            tab.setAttribute('aria-pressed', String(tab === button)),
          );
          this.querySelectorAll('[data-panel]').forEach((panel) => {
            panel.hidden = panel.dataset.panel !== button.dataset.group;
          });
        });
      }
    },
  );
}
if (!customElements.get('dd-purchase-plans')) {
  customElements.define(
    'dd-purchase-plans',
    class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        // Listen on product-info so Dawn's quantity input (outside this element) also reprices the card.
        this.root = this.closest('product-info') || this;
        this.root.addEventListener('change', () => this.updatePrice(), { signal: this.abort.signal });
        this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updatePrice());
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const next = data.html.querySelector('dd-purchase-plans [data-plan-content]');
          const current = this.querySelector('[data-plan-content]');
          if (!next || !current) return;
          const previous = this.querySelector('select')?.value;
          current.innerHTML = next.innerHTML;
          const select = this.querySelector('select');
          if (select && Array.from(select.options).some((option) => option.value === previous)) select.value = previous;
          this.updatePrice();
        });
        this.applySellingPlanFromUrl();
        this.updatePrice();
      }
      applySellingPlanFromUrl() {
        const select = this.querySelector('select');
        if (!select) return;
        const sellingPlanId = new URLSearchParams(window.location.search).get('selling_plan');
        if (!sellingPlanId) return;
        if (Array.from(select.options).some((option) => option.value === sellingPlanId)) select.value = sellingPlanId;
      }
      disconnectedCallback() {
        this.unsubscribe?.();
        this.unsubscribeQuantity?.();
        this.abort?.abort();
      }
      formatTotal(cents) {
        const quantity = Number(this.root.querySelector('.quantity__input')?.value || 1);
        return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(Number(cents) * quantity / 100);
      }
      updatePrice() {
        const select = this.querySelector('select');
        const price = this.querySelector('[data-plan-price]');
        const option = select?.selectedOptions[0];
        if (select && price) price.textContent = option?.dataset.cents && this.dataset.currency ? this.formatTotal(option.dataset.cents) : option?.dataset.price || '';
        const compare = this.querySelector('[data-plan-compare]');
        if (compare?.dataset.cents && this.dataset.currency) compare.textContent = this.formatTotal(compare.dataset.cents);
        this.updateSellingPlanUrl();
      }
      updateSellingPlanUrl() {
        const productInfo = this.closest('product-info');
        if (productInfo && productInfo.dataset.updateUrl === 'false') return;
        const select = this.querySelector('select');
        if (!select) return;
        const url = new URL(window.location.href);
        if (select.value) {
          url.searchParams.set('selling_plan', select.value);
        } else {
          url.searchParams.delete('selling_plan');
        }
        window.history.replaceState({}, '', url);
      }
    },
  );
}

if (!customElements.get('dd-product-heading')) {
  customElements.define(
    'dd-product-heading',
    class extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const heading = data.html.querySelector('dd-product-heading');
          if (heading) this.innerHTML = heading.innerHTML;
        });
      }
      disconnectedCallback() {
        this.unsubscribe?.();
      }
    },
  );
}

if (!customElements.get('dd-product-rating')) {
  customElements.define(
    'dd-product-rating',
    class extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const updated = data.html.querySelector('dd-product-rating');
          if (updated) this.innerHTML = updated.innerHTML;
        });
      }
      disconnectedCallback() {
        this.unsubscribe?.();
      }
    },
  );
}

if (!customElements.get('dd-product-description')) {
  customElements.define(
    'dd-product-description',
    class extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.section) return;
          const updated = data.html.querySelector('dd-product-description');
          if (updated) this.innerHTML = updated.innerHTML;
        });
      }
      disconnectedCallback() {
        this.unsubscribe?.();
      }
    },
  );
}

// Keep Dawn's quantity input as the single source of truth for cart and quantity rules.
if (!customElements.get('dd-product-controls')) {
  customElements.define('dd-product-controls', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.abort = new AbortController();
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-size-step]');
        if (!button) return;
        const radio = button.closest('[data-size-card]')?.querySelector('input[type="radio"]');
        const input = this.quantityInput;
        if (!input || button.disabled) return;
        if (radio && !radio.checked) {
          if (Number(button.dataset.sizeStep) < 0) return;
          this.pendingSelection = true;
          radio.click();
          this.sync(true);
          return;
        }
        if (this.pendingSelection) return;
        const next = Number(input.value) + Number(button.dataset.sizeStep) * Number(input.step || 1);
        input.value = Math.max(Number(input.min || 1), Math.min(input.max ? Number(input.max) : Infinity, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      }, { signal: this.abort.signal });
      this.root.addEventListener('change', (event) => {
        if (event.target === this.quantityInput) this.sync();
      }, { signal: this.abort.signal });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.section) return;
        this.pendingSelection = false;
        this.sync();
      });
      this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.sync(this.pendingSelection));
      this.frame = requestAnimationFrame(() => this.sync());
    }
    get quantityInput() { return this.root.querySelector('.product-form__quantity .quantity__input'); }
    sync(pending = false) {
      const input = this.quantityInput;
      const cards = this.root.querySelectorAll('[data-size-card]');
      this.root.classList.toggle('dd-has-size-quantity', Boolean(input && cards.length));
      if (!input) return;
      cards.forEach((card) => {
        const radio = card.querySelector('input[type="radio"]');
        const selected = radio.checked;
        card.querySelector('.dd-size-stepper').hidden = false;
        card.querySelector('output').textContent = selected ? input.value : '0';
        card.querySelector('[data-size-step="-1"]').disabled = pending || !selected || Number(input.value) <= Number(input.min || 1);
        card.querySelector('[data-size-step="1"]').disabled = pending || radio.classList.contains('disabled') || (selected && Boolean(input.max) && Number(input.value) >= Number(input.max));
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.unsubscribeQuantity?.();
      this.unsubscribe?.();
      this.abort?.abort();
    }
  });
}

if (!customElements.get('dd-product-extras')) {
  customElements.define('dd-product-extras', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-extra-toggle]');
        if (!button) return;
        const selected = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', String(selected));
        const action = selected ? (this.dataset.removeLabel || 'Remove') : (this.dataset.addLabel || 'Add');
        button.textContent = action;
        button.setAttribute('aria-label', `${action} ${button.dataset.title}`);
        button.closest('[data-extra-card]').classList.toggle('is-selected', selected);
        this.querySelector('[data-extra-status]').textContent = selected ? `${button.dataset.title} ${this.dataset.addedMessage || 'will be added with your food.'}` : `${button.dataset.title} ${this.dataset.removedMessage || 'removed from your selection.'}`;
      }, { signal: this.abort.signal });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-extra-variant]')) return;
        const option = event.target.selectedOptions[0];
        const card = event.target.closest('[data-extra-card]');
        const button = card.querySelector('[data-extra-toggle]');
        button.dataset.variantId = option.value;
        button.dataset.quantity = option.dataset.quantity;
        card.querySelector('[data-extra-price]').textContent = option.dataset.price;
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  });
}

if (!customElements.get('dd-size-tooltip')) {
  customElements.define('dd-size-tooltip', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const details = this.querySelector('details');
      const summary = this.querySelector('summary');
      const signal = this.abort.signal;
      const desktopHover = event => event.pointerType === 'mouse' && window.matchMedia('(min-width: 750px) and (hover: hover)').matches;
      this.addEventListener('pointerenter', event => {
        if (desktopHover(event)) details.open = true;
      }, { signal });
      this.addEventListener('pointerleave', event => {
        if (desktopHover(event)) details.open = false;
      }, { signal });
      summary.addEventListener('click', event => {
        // Hover already opened it; a mouse click must not immediately close it.
        // Touch and keyboard clicks retain the native details toggle.
        if (desktopHover(event)) { event.preventDefault(); details.open = true; }
      }, { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) details.open = false;
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.querySelector('details').open = false;
      }, { signal: this.abort.signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.querySelector('details').open = false;
          this.querySelector('summary').focus({ preventScroll: true });
          event.stopPropagation();
        }
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  });
}

if (!customElements.get('dd-buy-once')) {
  customElements.define('dd-buy-once', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.abort = new AbortController();
      this.root.addEventListener('change', () => this.updatePrice(), { signal: this.abort.signal });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.section) return;
        const next = data.html.querySelector('dd-buy-once');
        if (next) {
          this.dataset.price = next.dataset.price;
          this.innerHTML = next.innerHTML;
          this.updatePrice();
        }
      });
      this.unsubscribeQuantity = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updatePrice());
      this.frame = requestAnimationFrame(() => this.updatePrice());
    }
    updatePrice() {
      const quantity = Number(this.root.querySelector('.quantity__input')?.value || 1);
      this.querySelector('[data-once-price]').textContent = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(Number(this.dataset.price) * quantity / 100);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.unsubscribe?.();
      this.unsubscribeQuantity?.();
    }
  });
}
